import { db } from '@/db';
import { addDaysToDateKey, toLocalDateKey } from '@/domain/dates';
import {
  deferredOccurrenceForDate,
  isTemplateDueOnDate,
  occurrenceFromTemplate
} from '@/domain/day-rollover';
import { createId } from '@/domain/ids';
import { buildAuditEvent, evaluateStrictAction } from '@/domain/strict-mode';
import { awardXpOnce, XP_REWARDS } from '@/domain/xp';
import type { TaskFormData } from './task-form';
import type {
  LocalDateKey,
  RecurrenceRule,
  TaskOccurrence,
  TaskProgressEntry,
  TaskTemplate,
  Weekday
} from '@/types';

export class TaskActionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TaskActionError';
  }
}

function cleanOptional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function recurrenceFromTaskForm(data: TaskFormData): RecurrenceRule {
  const endDate = cleanOptional(data.recurrenceEndDate) as LocalDateKey | undefined;
  const bounds = { ...(endDate ? { endDate } : {}), paused: data.recurrencePaused };
  const startDate = data.date as LocalDateKey;
  const weekdays = data.recurrenceWeekdays.map((value) => Number(value) as Weekday);
  switch (data.recurrenceKind) {
    case 'once':
      return { kind: 'once', date: startDate, paused: false };
    case 'daily':
      return { kind: 'daily', startDate, intervalDays: data.recurrenceInterval, ...bounds };
    case 'weekdays':
      return {
        kind: 'weekdays',
        startDate,
        weekdays,
        intervalWeeks: data.recurrenceInterval,
        ...bounds
      };
    case 'weekly':
      return {
        kind: 'weekly',
        startDate,
        weekday: Number(data.recurrenceWeekday) as Weekday,
        intervalWeeks: data.recurrenceInterval,
        ...bounds
      };
    case 'monthly':
      return {
        kind: 'monthly',
        startDate,
        dayOfMonth: data.recurrenceDayOfMonth,
        intervalMonths: data.recurrenceInterval,
        overflow: data.recurrenceOverflow,
        ...bounds
      };
    case 'custom':
      return {
        kind: 'custom',
        startDate,
        interval: data.recurrenceInterval,
        unit: data.recurrenceUnit,
        ...(data.recurrenceUnit === 'weeks' && weekdays.length ? { weekdays } : {}),
        ...(data.recurrenceUnit === 'months'
          ? {
              dayOfMonth: data.recurrenceDayOfMonth,
              overflow: data.recurrenceOverflow
            }
          : {}),
        ...bounds
      };
  }
}

export async function createManualTask(data: TaskFormData): Promise<TaskOccurrence> {
  const now = new Date().toISOString();
  const id = createId();
  const task: TaskOccurrence = {
    id,
    occurrenceKey: `manual:${id}`,
    title: data.title.trim(),
    description: data.description.trim(),
    taskType: data.taskType,
    category: data.category,
    priority: data.priority,
    status: 'planned',
    date: data.date as LocalDateKey,
    ...(cleanOptional(data.startTime)
      ? { startTime: data.startTime as TaskOccurrence['startTime'] }
      : {}),
    ...(cleanOptional(data.endTime) ? { endTime: data.endTime as TaskOccurrence['endTime'] } : {}),
    ...(data.estimatedMinutes !== undefined ? { estimatedMinutes: data.estimatedMinutes } : {}),
    ...(data.targetValue !== undefined ? { targetValue: data.targetValue } : {}),
    ...(cleanOptional(data.unit) ? { unit: data.unit.trim() } : {}),
    currentValue: 0,
    strictMode: data.strictMode,
    ...(cleanOptional(data.projectId) ? { projectId: data.projectId } : {}),
    notes: data.notes.trim(),
    sortOrder: Date.now(),
    createdAt: now,
    updatedAt: now
  };
  await db.transaction('rw', db.taskOccurrences, db.auditEvents, async () => {
    await db.taskOccurrences.add(task);
    await db.auditEvents.add(
      buildAuditEvent({
        entityType: 'TaskOccurrence',
        entityId: task.id,
        action: 'create',
        date: task.date,
        after: { title: task.title, status: task.status }
      })
    );
  });
  return task;
}

export async function createTaskTemplate(data: TaskFormData): Promise<TaskTemplate> {
  const now = new Date().toISOString();
  const id = createId();
  const template: TaskTemplate = {
    id,
    title: data.title.trim(),
    description: data.description.trim(),
    taskType: data.taskType,
    category: data.category,
    priority: data.priority,
    recurrence: recurrenceFromTaskForm(data),
    ...(cleanOptional(data.startTime)
      ? { defaultStartTime: data.startTime as TaskTemplate['defaultStartTime'] }
      : {}),
    ...(cleanOptional(data.endTime)
      ? { defaultEndTime: data.endTime as TaskTemplate['defaultEndTime'] }
      : {}),
    ...(data.estimatedMinutes !== undefined ? { estimatedMinutes: data.estimatedMinutes } : {}),
    ...(data.targetValue !== undefined ? { defaultTargetValue: data.targetValue } : {}),
    ...(cleanOptional(data.unit) ? { unit: data.unit.trim() } : {}),
    strictMode: data.strictMode,
    ...(cleanOptional(data.projectId) ? { projectId: data.projectId } : {}),
    active: true,
    effectiveFrom: data.date as LocalDateKey,
    ...(cleanOptional(data.recurrenceEndDate)
      ? { effectiveUntil: data.recurrenceEndDate as LocalDateKey }
      : {}),
    revision: 1,
    sortOrder: Date.now(),
    notes: data.notes.trim(),
    tags: [],
    createdAt: now,
    updatedAt: now
  };
  await db.transaction('rw', db.taskTemplates, db.taskOccurrences, async () => {
    await db.taskTemplates.add(template);
    const today = toLocalDateKey(new Date());
    if (isTemplateDueOnDate(template, today)) {
      await db.taskOccurrences.add(occurrenceFromTemplate(template, today, now));
    }
  });
  return template;
}

async function awardTaskXp(task: TaskOccurrence, nowIso: string): Promise<void> {
  const sourceType = task.priority === 'mandatory' ? 'mandatory-task' : 'task';
  const sourceKey = `${sourceType}:${task.id}`;
  const existing = await db.xpEvents.where('sourceKey').equals(sourceKey).toArray();
  const reward = task.priority === 'mandatory' ? XP_REWARDS.mandatoryTask : XP_REWARDS.normalTask;
  const previous = existing[0];
  if (previous) {
    if (previous.amount !== reward) {
      await db.xpEvents.put({
        ...previous,
        amount: reward,
        correctedAt: nowIso,
        updatedAt: nowIso
      });
    }
    return;
  }
  const event = awardXpOnce(existing, {
    sourceType,
    sourceId: task.id,
    date: task.date,
    amount: reward,
    reason: `${task.title} erledigt`,
    awardedAt: nowIso
  });
  if (event) await db.xpEvents.add(event);
}

export async function completeTask(taskId: string): Promise<TaskOccurrence> {
  return db.transaction('rw', db.taskOccurrences, db.xpEvents, db.auditEvents, async () => {
    const task = await db.taskOccurrences.get(taskId);
    if (!task) throw new TaskActionError('Die Aufgabe wurde nicht gefunden.');
    if (task.status === 'completed') return task;
    const decision = evaluateStrictAction({ action: 'complete', occurrence: task });
    if (!decision.allowed)
      throw new TaskActionError(
        decision.violations[0]?.message ?? 'Diese Aktion ist im strikten Modus nicht erlaubt.'
      );
    const now = new Date().toISOString();
    const completed: TaskOccurrence = {
      ...task,
      status: 'completed',
      currentValue: task.targetValue
        ? Math.max(task.currentValue, task.targetValue)
        : task.currentValue,
      completedAt: now,
      updatedAt: now
    };
    await db.taskOccurrences.put(completed);
    await awardTaskXp(completed, now);
    await db.auditEvents.add(
      buildAuditEvent({
        entityType: 'TaskOccurrence',
        entityId: task.id,
        action: 'complete',
        date: task.date,
        strictMode: task.strictMode,
        before: { status: task.status },
        after: { status: 'completed' },
        at: now
      })
    );
    return completed;
  });
}

export async function addTaskProgress(
  taskId: string,
  delta: number,
  note?: string,
  kind?: TaskProgressEntry['kind']
): Promise<TaskOccurrence> {
  if (!Number.isFinite(delta) || delta === 0)
    throw new TaskActionError('Der Fortschrittswert ist ungültig.');
  return db.transaction(
    'rw',
    db.taskOccurrences,
    db.taskProgressEntries,
    db.xpEvents,
    db.auditEvents,
    async () => {
      const task = await db.taskOccurrences.get(taskId);
      if (!task) throw new TaskActionError('Die Aufgabe wurde nicht gefunden.');
      const now = new Date().toISOString();
      const resultingValue = Math.max(0, task.currentValue + delta);
      const reachesTarget = Boolean(task.targetValue && resultingValue >= task.targetValue);
      const reopensCompletedTask = Boolean(
        task.status === 'completed' && task.targetValue && resultingValue < task.targetValue
      );
      const updated: TaskOccurrence = {
        ...task,
        currentValue: resultingValue,
        ...(reachesTarget
          ? { status: 'completed' as const, completedAt: task.completedAt ?? now }
          : reopensCompletedTask
            ? { status: 'paused' as const, completedAt: undefined }
            : {}),
        updatedAt: now
      };
      const entry: TaskProgressEntry = {
        id: createId(),
        occurrenceId: task.id,
        date: task.date,
        kind: kind ?? (delta > 0 ? 'increment' : 'correction'),
        delta,
        resultingValue,
        ...(task.unit ? { unit: task.unit } : {}),
        ...(note ? { note } : {}),
        createdAt: now,
        updatedAt: now
      };
      await db.taskOccurrences.put(updated);
      await db.taskProgressEntries.add(entry);
      if (reopensCompletedTask) {
        const sourceType = task.priority === 'mandatory' ? 'mandatory-task' : 'task';
        const sourceKey = `${sourceType}:${task.id}`;
        const reward = await db.xpEvents.where('sourceKey').equals(sourceKey).first();
        if (reward) {
          await db.xpEvents.put({
            ...reward,
            amount: 0,
            correctedAt: now,
            updatedAt: now
          });
        }
      }
      if (reachesTarget && task.status !== 'completed') await awardTaskXp(updated, now);
      return updated;
    }
  );
}

export async function addManualTaskTime(
  taskId: string,
  minutes: number,
  note?: string
): Promise<TaskOccurrence> {
  const task = await db.taskOccurrences.get(taskId);
  const unit = task?.unit?.trim().toLowerCase();
  if (!task) throw new TaskActionError('Die Aufgabe wurde nicht gefunden.');
  if (!['minute', 'minutes', 'minuten', 'min'].includes(unit ?? '')) {
    throw new TaskActionError(
      'Manuelle Zeit kann nur einer Minuten-Aufgabe gutgeschrieben werden.'
    );
  }
  if (!Number.isInteger(minutes) || minutes <= 0) {
    throw new TaskActionError('Trage eine positive Anzahl voller Minuten ein.');
  }
  return addTaskProgress(taskId, minutes, note, 'manual-time');
}

export interface TaskOccurrenceUpdate {
  title: string;
  description: string;
  date: LocalDateKey;
  startTime?: TaskOccurrence['startTime'];
  endTime?: TaskOccurrence['endTime'];
  estimatedMinutes?: number;
  targetValue?: number;
  unit?: string;
  projectId?: string;
  notes: string;
}

export async function updateTaskOccurrence(
  taskId: string,
  update: TaskOccurrenceUpdate
): Promise<TaskOccurrence> {
  return db.transaction('rw', db.taskOccurrences, db.auditEvents, async () => {
    const task = await db.taskOccurrences.get(taskId);
    if (!task) throw new TaskActionError('Die Aufgabe wurde nicht gefunden.');
    if (update.title.trim().length < 2) {
      throw new TaskActionError('Der Titel muss mindestens zwei Zeichen enthalten.');
    }
    if (
      update.targetValue !== undefined &&
      task.targetValue !== undefined &&
      update.targetValue < task.targetValue
    ) {
      const decision = evaluateStrictAction({
        action: 'reduce-target',
        occurrence: task,
        newTargetValue: update.targetValue
      });
      if (!decision.allowed) {
        throw new TaskActionError(
          decision.violations[0]?.message ?? 'Das Ziel kann heute nicht reduziert werden.'
        );
      }
    }
    const changesProtectedFields =
      update.title.trim() !== task.title ||
      update.description.trim() !== task.description ||
      (!task.templateId && update.date !== task.date) ||
      update.startTime !== task.startTime ||
      update.endTime !== task.endTime ||
      update.estimatedMinutes !== task.estimatedMinutes ||
      update.targetValue !== task.targetValue ||
      cleanOptional(update.unit ?? '') !== task.unit ||
      (update.projectId || undefined) !== task.projectId;
    if (task.strictMode !== 'off' && changesProtectedFields) {
      throw new TaskActionError(
        'Änderungen an einer strikten Tagesaufgabe gelten frühestens morgen. Passe dafür die Wiederholung im Plan an.'
      );
    }
    const now = new Date().toISOString();
    const updated: TaskOccurrence = {
      ...task,
      title: update.title.trim(),
      description: update.description.trim(),
      date: task.templateId ? task.date : update.date,
      startTime: update.startTime,
      endTime: update.endTime,
      estimatedMinutes: update.estimatedMinutes,
      targetValue: update.targetValue,
      unit: cleanOptional(update.unit ?? ''),
      projectId: update.projectId || undefined,
      notes: update.notes.trim(),
      updatedAt: now
    };
    await db.taskOccurrences.put(updated);
    await db.auditEvents.add(
      buildAuditEvent({
        entityType: 'TaskOccurrence',
        entityId: task.id,
        action: 'update',
        date: task.date,
        strictMode: task.strictMode,
        before: {
          title: task.title,
          date: task.date,
          targetValue: task.targetValue,
          projectId: task.projectId
        },
        after: {
          title: updated.title,
          date: updated.date,
          targetValue: updated.targetValue,
          projectId: updated.projectId
        },
        at: now
      })
    );
    return updated;
  });
}

export async function deferTaskToTomorrow(taskId: string): Promise<TaskOccurrence> {
  return db.transaction('rw', db.taskOccurrences, db.auditEvents, async () => {
    const task = await db.taskOccurrences.get(taskId);
    if (!task) throw new TaskActionError('Die Aufgabe wurde nicht gefunden.');
    if (['completed', 'deferred', 'missed', 'skipped'].includes(task.status))
      throw new TaskActionError('Diese Aufgabe kann nicht mehr verschoben werden.');
    const target = addDaysToDateKey(task.date, 1);
    const now = new Date().toISOString();
    const deferred: TaskOccurrence = {
      ...task,
      status: 'deferred',
      deferredToDate: target,
      updatedAt: now
    };
    const copy = deferredOccurrenceForDate(deferred, target, now);
    await db.taskOccurrences.put(deferred);
    if (!(await db.taskOccurrences.where('occurrenceKey').equals(copy.occurrenceKey).first()))
      await db.taskOccurrences.add(copy);
    await db.auditEvents.add(
      buildAuditEvent({
        entityType: 'TaskOccurrence',
        entityId: task.id,
        action: 'defer',
        date: task.date,
        strictMode: task.strictMode,
        before: { date: task.date, status: task.status },
        after: { date: target, status: 'deferred' },
        at: now
      })
    );
    return deferred;
  });
}

export async function skipTask(
  taskId: string,
  options: { confirmation?: string; reason: string; waitStartedAt?: string }
): Promise<TaskOccurrence> {
  return db.transaction('rw', db.taskOccurrences, db.auditEvents, async () => {
    const task = await db.taskOccurrences.get(taskId);
    if (!task) throw new TaskActionError('Die Aufgabe wurde nicht gefunden.');
    const decision = evaluateStrictAction({ action: 'skip', occurrence: task, ...options });
    if (!decision.allowed)
      throw new TaskActionError(decision.violations.map((item) => item.message).join(' '));
    const now = new Date().toISOString();
    const skipped: TaskOccurrence = {
      ...task,
      status: 'skipped',
      skippedAt: now,
      skipReason: options.reason.trim(),
      updatedAt: now
    };
    await db.taskOccurrences.put(skipped);
    await db.auditEvents.add(
      buildAuditEvent({
        entityType: 'TaskOccurrence',
        entityId: task.id,
        action: 'skip',
        date: task.date,
        strictMode: task.strictMode,
        reason: options.reason,
        before: { status: task.status },
        after: { status: 'skipped' },
        at: now
      })
    );
    return skipped;
  });
}

export function defaultTaskFormData(
  date = toLocalDateKey(new Date()),
  recurrenceKind: TaskFormData['recurrenceKind'] = 'once'
): TaskFormData {
  return {
    title: '',
    description: '',
    date,
    startTime: '',
    endTime: '',
    estimatedMinutes: 25,
    targetValue: undefined,
    unit: 'Minuten',
    taskType: 'task',
    category: 'general',
    priority: 'normal',
    strictMode: 'off',
    projectId: '',
    notes: '',
    recurrenceKind,
    recurrenceInterval: 1,
    recurrenceWeekdays: [],
    recurrenceWeekday: String(
      new Date(`${date}T12:00:00`).getDay()
    ) as TaskFormData['recurrenceWeekday'],
    recurrenceDayOfMonth: Number(date.slice(-2)),
    recurrenceUnit: 'days',
    recurrenceOverflow: 'last-day',
    recurrenceEndDate: '',
    recurrencePaused: false
  };
}
